import { useEffect, useRef, useState } from "react";
import { Brush, Droplet, PaintBucket, RotateCcw, Undo2 } from "lucide-react";
import { hexToRgb } from "@/lib/palettes";
import { imageDataToCanvas } from "@/lib/heightMapUtils";
import { TopoBackground } from "@/components/height-map/TopoBackground";
import { MaterialSelect } from "@/components/height-map/MaterialSelect";

/**
 * Exact Paint canvas. Tools: Solid Brush, Gradient Brush, Fill Bucket.
 *
 * Paint strokes are drawn onto a SEPARATE transparent overlay canvas (not the
 * base color map). The overlay is lifted up to the parent via onOverlayChange
 * so it can be composited on top of Gradient Map and Advanced too — and stays
 * locked there because only Exact Paint edits the overlay.
 *
 * The Solid Brush stamps hard-edged, fully opaque pixels (no anti-aliased /
 * blurred rim). Right-click + drag pans the view when zoomed.
 */
export const PaintCanvas = ({
  baseImageData,
  paintCanvasRef,
  layers,
  activeLayerId,
  onSelectLayer,
  paintKey,
  paintOverlay,
  paintSyncToken,
  onOverlayChange,
  referenceImageData,
  showReference,
  referenceOpacity = 0.4,
  onBeforeStroke,
  onUndo,
  canUndo,
  floatingToolbar = false,
  viewportHeight = 560,
  roundedMapPreview = false,
}) => {
  const containerRef = useRef(null);
  const displayCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const strokingRef = useRef(false);
  const lastPointRef = useRef(null);

  const [tool, setTool] = useState("brush");
  const [brushSize, setBrushSize] = useState(24);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    if (paintCanvasRef) paintCanvasRef.current = displayCanvasRef.current;
  });

  const getOverlay = () => {
    if (!overlayCanvasRef.current) {
      overlayCanvasRef.current = document.createElement("canvas");
    }
    return overlayCanvasRef.current;
  };
  const overlayCtx = () => getOverlay().getContext("2d", { willReadFrequently: true });
  const displayCtx = () =>
    displayCanvasRef.current?.getContext("2d", { willReadFrequently: true });

  const composite = () => {
    const c = displayCanvasRef.current;
    if (!c || !baseImageData) return;
    const ctx = displayCtx();
    ctx.putImageData(baseImageData, 0, 0);
    ctx.drawImage(getOverlay(), 0, 0);
    if (
      showReference &&
      referenceImageData &&
      referenceImageData.width === c.width &&
      referenceImageData.height === c.height
    ) {
      ctx.save();
      ctx.globalAlpha = referenceOpacity;
      ctx.drawImage(imageDataToCanvas(referenceImageData), 0, 0);
      ctx.restore();
    }
  };

  useEffect(() => {
    composite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReference, referenceOpacity]);

  useEffect(() => {
    const c = displayCanvasRef.current;
    if (!baseImageData || !c) return;
    const w = baseImageData.width;
    const h = baseImageData.height;
    c.width = w;
    c.height = h;
    const overlay = getOverlay();
    overlay.width = w;
    overlay.height = h;
    const octx = overlayCtx();
    octx.clearRect(0, 0, w, h);
    if (paintOverlay && paintOverlay.width === w && paintOverlay.height === h) {
      octx.putImageData(paintOverlay, 0, 0);
    }
    composite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseImageData, paintKey, paintSyncToken]);

  const activeLayer = layers.find((l) => l.id === activeLayerId) || layers[0];
  const effectiveColor = activeLayer ? activeLayer.color : "#ffffff";

  const canvasCoords = (e) => {
    const c = displayCanvasRef.current;
    if (!c) return null;
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const stampSolid = (octx, cx, cy, radius, rgb) => {
    const w = octx.canvas.width;
    const h = octx.canvas.height;
    const r = Math.max(1, radius);
    const x0 = Math.max(0, Math.floor(cx - r));
    const y0 = Math.max(0, Math.floor(cy - r));
    const x1 = Math.min(w - 1, Math.ceil(cx + r));
    const y1 = Math.min(h - 1, Math.ceil(cy + r));
    if (x1 < x0 || y1 < y0) return;
    const bw = x1 - x0 + 1;
    const bh = y1 - y0 + 1;
    const img = octx.getImageData(x0, y0, bw, bh);
    const d = img.data;
    const rr = r * r;
    for (let yy = 0; yy < bh; yy++) {
      for (let xx = 0; xx < bw; xx++) {
        const dx = x0 + xx - cx;
        const dy = y0 + yy - cy;
        if (dx * dx + dy * dy <= rr) {
          const k = (yy * bw + xx) * 4;
          d[k] = rgb.r;
          d[k + 1] = rgb.g;
          d[k + 2] = rgb.b;
          d[k + 3] = 255;
        }
      }
    }
    octx.putImageData(img, x0, y0);
  };

  const stampSoft = (octx, cx, cy, radius, rgb) => {
    const r = Math.max(1, radius);
    const rad = octx.createRadialGradient(cx, cy, 0, cx, cy, r);
    rad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},1)`);
    rad.addColorStop(0.7, `rgba(${rgb.r},${rgb.g},${rgb.b},0.8)`);
    rad.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
    octx.save();
    octx.fillStyle = rad;
    octx.beginPath();
    octx.arc(cx, cy, r, 0, Math.PI * 2);
    octx.fill();
    octx.restore();
  };

  const drawLine = (ptA, ptB) => {
    const octx = overlayCtx();
    const rgb = hexToRgb(effectiveColor);
    const dist = Math.hypot(ptB.x - ptA.x, ptB.y - ptA.y);
    const step = Math.max(1, brushSize / 4);
    const count = Math.ceil(dist / step);
    for (let i = 0; i <= count; i++) {
      const t = count === 0 ? 0 : i / count;
      const x = ptA.x + (ptB.x - ptA.x) * t;
      const y = ptA.y + (ptB.y - ptA.y) * t;
      if (tool === "brush") {
        stampSolid(octx, x, y, brushSize / 2, rgb);
      } else {
        stampSoft(octx, x, y, brushSize / 2, rgb);
      }
    }
  };

  const commitOverlay = () => {
    if (!onOverlayChange) return;
    const o = getOverlay();
    const ctx = overlayCtx();
    const data = ctx.getImageData(0, 0, o.width, o.height);
    onOverlayChange(data);
  };

  const floodFill = (startPt) => {
    const octx = overlayCtx();
    const w = octx.canvas.width;
    const h = octx.canvas.height;
    const sx = Math.floor(startPt.x);
    const sy = Math.floor(startPt.y);
    if (sx < 0 || sx >= w || sy < 0 || sy >= h) return;

    if (onBeforeStroke) {
      const currentOverlay = octx.getImageData(0, 0, w, h);
      onBeforeStroke(currentOverlay);
    }

    const dCtx = displayCtx();
    const dispData = dCtx.getImageData(0, 0, w, h);
    const disp = dispData.data;
    const idx0 = (sy * w + sx) * 4;
    const targetR = disp[idx0];
    const targetG = disp[idx0 + 1];
    const targetB = disp[idx0 + 2];
    const targetA = disp[idx0 + 3];

    const rgb = hexToRgb(effectiveColor);
    if (targetR === rgb.r && targetG === rgb.g && targetB === rgb.b && targetA === 255) return;

    const overlayImg = octx.getImageData(0, 0, w, h);
    const od = overlayImg.data;

    const tolerance = 16;
    const matches = (i) =>
      Math.abs(disp[i] - targetR) <= tolerance &&
      Math.abs(disp[i + 1] - targetG) <= tolerance &&
      Math.abs(disp[i + 2] - targetB) <= tolerance &&
      Math.abs(disp[i + 3] - targetA) <= tolerance;

    const visited = new Uint8Array(w * h);
    const queue = [sy * w + sx];
    visited[sy * w + sx] = 1;

    let head = 0;
    while (head < queue.length) {
      const curr = queue[head++];
      const cy = Math.floor(curr / w);
      const cx = curr % w;
      const i = curr * 4;

      od[i] = rgb.r;
      od[i + 1] = rgb.g;
      od[i + 2] = rgb.b;
      od[i + 3] = 255;

      const neighbors = [];
      if (cx > 0) neighbors.push(curr - 1);
      if (cx < w - 1) neighbors.push(curr + 1);
      if (cy > 0) neighbors.push(curr - w);
      if (cy < h - 1) neighbors.push(curr + w);

      for (let n = 0; n < neighbors.length; n++) {
        const nIdx = neighbors[n];
        if (!visited[nIdx]) {
          visited[nIdx] = 1;
          if (matches(nIdx * 4)) queue.push(nIdx);
        }
      }
    }

    octx.putImageData(overlayImg, 0, 0);
    composite();
    commitOverlay();
  };

  const onPointerDown = (e) => {
    if (e.button === 2) {
      dragRef.current = { startX: e.clientX, startY: e.clientY, initPan: { ...pan } };
      e.target.setPointerCapture(e.pointerId);
      return;
    }
    const pt = canvasCoords(e);
    if (!pt) return;
    if (tool === "bucket") {
      floodFill(pt);
      return;
    }

    if (onBeforeStroke) {
      const octx = overlayCtx();
      const currentOverlay = octx.getImageData(0, 0, octx.canvas.width, octx.canvas.height);
      onBeforeStroke(currentOverlay);
    }

    strokingRef.current = true;
    lastPointRef.current = pt;
    const octx = overlayCtx();
    const rgb = hexToRgb(effectiveColor);
    if (tool === "brush") stampSolid(octx, pt.x, pt.y, brushSize / 2, rgb);
    else stampSoft(octx, pt.x, pt.y, brushSize / 2, rgb);
    composite();
    e.target.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (dragRef.current) {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPan({ x: dragRef.current.initPan.x + dx, y: dragRef.current.initPan.y + dy });
      return;
    }
    if (!strokingRef.current) return;
    const pt = canvasCoords(e);
    if (!pt || !lastPointRef.current) return;
    drawLine(lastPointRef.current, pt);
    lastPointRef.current = pt;
    composite();
  };

  const onPointerUp = (e) => {
    if (dragRef.current) {
      dragRef.current = null;
      try { e.target.releasePointerCapture(e.pointerId); } catch {}
      return;
    }
    if (strokingRef.current) {
      strokingRef.current = false;
      lastPointRef.current = null;
      try { e.target.releasePointerCapture(e.pointerId); } catch {}
      commitOverlay();
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const pixelDelta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1);
      const zoomStep = Math.max(-0.32, Math.min(0.32, -pixelDelta * 0.0015));
      const factor = Math.exp(zoomStep);
      setZoom((z) => {
        const nz = Math.max(1, Math.min(10, z * factor));
        setPan((p) => {
          if (nz === 1) return { x: 0, y: 0 };
          const scale = nz / z;
          return {
            x: p.x * scale + cx * (1 - scale),
            y: p.y * scale + cy * (1 - scale),
          };
        });
        return nz;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const clearPainted = () => {
    if (onBeforeStroke) {
      const octx = overlayCtx();
      const currentOverlay = octx.getImageData(0, 0, octx.canvas.width, octx.canvas.height);
      onBeforeStroke(currentOverlay);
    }
    const o = getOverlay();
    const octx = overlayCtx();
    octx.clearRect(0, 0, o.width, o.height);
    composite();
    commitOverlay();
  };

  const toolbarMarkup = (
    <div
      className={
        floatingToolbar
          ? "absolute top-3 left-1/2 -translate-x-1/2 z-20 flex flex-wrap items-center gap-2 px-3 py-1.5 bg-[#101116]/90 backdrop-blur-md border border-zinc-800/80 rounded-xl shadow-2xl"
          : "flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-zinc-800/60"
      }
    >
      <div className="flex items-center gap-1 bg-[#0d0e12] rounded-lg p-1 border border-zinc-800/70">
        <ToolBtn testId="tool-brush" active={tool === "brush"} onClick={() => setTool("brush")} icon={<Brush className="w-3.5 h-3.5" />} label="Solid Brush" />
        <ToolBtn testId="tool-gradient" active={tool === "gradient"} onClick={() => setTool("gradient")} icon={<Droplet className="w-3.5 h-3.5" />} label="Gradient Brush" />
        <ToolBtn testId="tool-bucket" active={tool === "bucket"} onClick={() => setTool("bucket")} icon={<PaintBucket className="w-3.5 h-3.5" />} label="Fill Bucket" />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Size</label>
        <input
          data-testid="brush-size"
          type="range"
          min={2}
          max={120}
          value={brushSize}
          onChange={(e) => setBrushSize(Number(e.target.value))}
          className="w-28 accent-rose-500"
        />
        <span className="text-[10px] mono text-zinc-400 w-5">{brushSize}</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Material</label>
        <MaterialSelect
          testId="active-layer-select"
          layers={layers}
          activeId={activeLayer?.id}
          onSelect={onSelectLayer}
        />
      </div>
      <div className="ml-auto flex items-center gap-2">
        <button
          data-testid="paint-undo"
          onClick={onUndo}
          disabled={!canUndo}
          className={`text-[11px] font-medium border rounded-lg px-2.5 py-1 transition flex items-center gap-1.5 ${
            canUndo
              ? "text-zinc-300 hover:text-white bg-zinc-800/70 hover:bg-zinc-700/80 border-zinc-700/60"
              : "text-zinc-600 border-zinc-800 opacity-50 cursor-not-allowed"
          }`}
        >
          <Undo2 className="w-3 h-3" /> Undo
        </button>
        <button
          data-testid="paint-clear"
          onClick={clearPainted}
          className="text-[11px] font-medium text-zinc-300 hover:text-white bg-zinc-800/70 hover:bg-zinc-700/80 border border-zinc-700/60 rounded-lg px-2.5 py-1 transition flex items-center gap-1.5"
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
    </div>
  );

  return (
    <div className={`w-full relative overflow-hidden flex flex-col ${floatingToolbar ? "h-full rounded-xl bg-[#090a0d]" : "rounded-2xl border border-zinc-800/80 bg-[#101116]"}`}>
      {toolbarMarkup}
      <div
        ref={containerRef}
        className={`relative w-full overflow-hidden cursor-crosshair flex items-center justify-center ${floatingToolbar ? "h-full flex-1" : "flex-none"}`}
        style={floatingToolbar ? { touchAction: "none" } : { height: viewportHeight, touchAction: "none" }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <TopoBackground seed={17} />
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
            transformOrigin: "center center",
            transition: dragRef.current
              ? "none"
              : "transform 110ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
          }}
        >
          <canvas
            data-testid="paint-canvas"
            ref={displayCanvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            className={`max-w-full max-h-full object-contain ${roundedMapPreview ? "rounded-xl" : ""}`}
            style={{ imageRendering: "pixelated" }}
          />
        </div>
        <div className="absolute bottom-2 left-2 text-[10px] mono bg-black/60 backdrop-blur-sm text-zinc-300 px-2.5 py-1 rounded-md border border-zinc-800/60 pointer-events-none">
          Scroll to zoom · Right-click drag to pan · Ctrl/Cmd+Z to undo
          {zoom > 1.02 && <span> · {zoom.toFixed(1)}x</span>}
        </div>
      </div>
    </div>
  );
};

const ToolBtn = ({ active, onClick, icon, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
      active ? "brand-gradient text-white shadow-md shadow-rose-500/25" : "text-zinc-400 hover:text-zinc-100"
    }`}
  >
    {icon}
    {label}
  </button>
);
